import React, { useEffect, useState, useCallback } from 'react';
import connect from 'react-redux/lib/connect/connect';
import { bindActionCreators } from 'redux';
import { Col, Grid, Row, Tab, Tabs, Breadcrumb, BreadcrumbItem } from 'react-bootstrap';
import { FiBell } from "react-icons/fi";
import { LinkContainer } from "react-router-bootstrap";
import { adminActions } from '../../../../state/modules/admin';
import { industryActions } from '../../../../state/modules/industry';
import { riskThresholds, routes } from '../../../../constants';
import { SortableTable, LoadingButton, SearchForm, FormSubmissionMessage, ConfirmationModal } from '../../../components';
import { useDebounce } from '../../../../utils';

const endOfYearReportTake = 20;

const defaultIndustryId = 1;

const defaultFetchBusinessUsersQuery = {
  skip: 0,
  take: endOfYearReportTake,
  tags: '',
  industryIds: defaultIndustryId,
  yearsFromNow: 0
};

const mapRiskRate = (riskRate) =>
  riskRate < riskThresholds.medium ? 'Low' :
    riskRate < riskThresholds.high ?
      'Medium' : 'High';

const mapRiskRateStyle = (riskRate) =>
  !riskRate ? 'riskRating' :
    (riskRate === 'Low' || riskRate === '-') ? 'low-risk' :
      riskRate === 'Medium' ? 'medium-risk' :
        'high-risk';

const endOfYearReportColumns = [
  {
    name: 'businessName',
    display: () => 'Business Name',
    className: () => 'business-name',
    sortable: true
  },
  {
    name: 'userName',
    display: () => 'User name',
    className: () => 'user-name',
    sortable: true
  },
  {
    name: 'progress',
    display: () => 'Progress',
    className: () => '',
    sortable: true
  },
  {
    name: 'riskRating',
    display: () => 'Risk rating',
    className: mapRiskRateStyle,
  },
  {
    name: 'actions',
    display: () => 'Actions',
    className: () => '',
  }
];

const endOfYearReportRows = (userData, notificationButtonOnClick) => {

  if (userData === undefined) {
    return []
  }
  const users = userData.usersCpdStats;
  return users.map((userCpdStat, index) => {
    const data = { userEmail: userCpdStat.email };
    return {
      id: `end-of-year-report-${index}`,
      businessName: userCpdStat.businessName || "",
      userName: `${userCpdStat.firstName} ${userCpdStat.lastName}`,
      progress: userCpdStat.derivedStat.progress,
      riskRating: mapRiskRate(userCpdStat.derivedStat.riskRating),
      actions: (
        <span
          className='send-reminder'
          onClick={() => notificationButtonOnClick(data, `${userCpdStat.firstName} ${userCpdStat.lastName}`)}
        >
          <FiBell />
        </span >
      )
    };
  })
};

const ReportBreadCrumb = () =>(
  <Breadcrumb>
    <LinkContainer to={routes.adminPortal.root}>
      <BreadcrumbItem>
        Admin portal
      </BreadcrumbItem>
    </LinkContainer>
    <BreadcrumbItem active>
      End of Year Report
        </BreadcrumbItem>
  </Breadcrumb>
);

/**
 * Renders the table to display users' cpd progress.
 */
const EndOfYearReportTable = ({
  fetchQuery,
  setFetchQuery,
  searchText,
  setSearchText,
  isFetchingReport,
  userCpdProgressReport,
  sortBy,
  notificationButtonOnClick
}) => {
  const isFetching = fetchQuery.skip !== 0 && isFetchingReport;

  const isFetchingAll = fetchQuery.skip === 0 && isFetchingReport;

  const reportData = userCpdProgressReport;

  const userEndOfYearReportRows = endOfYearReportRows(reportData, notificationButtonOnClick)

  const hasMore = reportData !== undefined && reportData.usersCpdStats.length < reportData.total;

  const onLoadMoreUsers = useCallback(() => {
    const newQuery = {
        ...fetchQuery,
        skip: fetchQuery.skip + endOfYearReportTake
    };
    setFetchQuery(newQuery);
  });

  const onSearchUsers = (e) => setSearchText(e.target.value);

  return (
    <div>
      <Row className='no-gutter top-gap'>
        <Col md={3} xs={12}>
          <SearchForm label="Search users" searchQuery={searchText} onSearchChange={onSearchUsers} />
        </Col>
      </Row>
      <Row className='no-gutter'>
        <Col xs={12}>
          <section className='report-table-section'>
            <div className="container">
              <SortableTable
                className='table--progress-report'
                columns={endOfYearReportColumns}
                rows={userEndOfYearReportRows}
                sortBy={sortBy}
                isFetchingAll={isFetchingAll}
                emptyData={"No user Found"}
              />
            </div>
          </section>
        </Col>
      </Row>
      {
        hasMore ? 
        <LoadingButton text='Load More' loading={isFetching} onClick={onLoadMoreUsers} /> : 
        <React.Fragment></React.Fragment>
      }
    </div>
  )
}

/**
 * Renders the page to display end of year report.
 */
const EndOfYearReport = ({
  industries,
  getIndustries,
  getUserCpdProgressReport,
  isSendingNotification,
  notificationError,
  sendUserCpdProgressNotification,
  ...props
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState('');
  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);
  const notificationMessage = isSendingNotification 
    ? "Sending Notification ..." 
    :  "Notification sent!";

  const [showSubmissionMessage, setShowSubmissionMessage] = useState(false);
  const openSubmissionMessage = () => setShowSubmissionMessage(true);
  const closeSubmissionMessage = () => setShowSubmissionMessage(false);

  const [queryIndustryKey, setQueryIndustryKey] = useState(defaultIndustryId);
  const [searchText, setSearchText] = useState("");
  const [sort, setSortOrder] = useState();

  const [notificationData, setNotificationData] = useState(undefined);

  const [fetchQuery, setFetchQuery] = useState(defaultFetchBusinessUsersQuery);
  // Debounce the fetch query
  const debouncedFetchQuery = useDebounce(fetchQuery, 500);

  const sortBy = (field, sortOrder) => {
    const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
    setSortOrder(sortParam)
  };

  const notificationButtonOnClick = (data, userName) => {
    setNotificationData(data);
    setModalBody(`Do you want to send notification to ${userName}?`);
    handleShow();
  }

  const sendButtonOnClick = () => {
    if (notificationData !== undefined) {
      sendUserCpdProgressNotification(notificationData);
      openSubmissionMessage();
    }
    handleClose();
  }

  // Get industries when industries is undefined
  useEffect(() => {
    if (industries === undefined){
      getIndustries();
    }
  }, [industries]);

  // Update query when query params changed
  useEffect(() => {
    setFetchQuery({
      ...fetchQuery,
      skip: 0,
      industryIds: queryIndustryKey,
      tags: searchText,
      sort: sort
    })
  }, [queryIndustryKey, sort, searchText]);

  // Search user when query changed
  useEffect(() => {
    getUserCpdProgressReport(debouncedFetchQuery);
  }, [debouncedFetchQuery]);

  return (
    <div className='base-layout'>
      <section className='layout--header'>
        <Grid>
          <Row className='sm-gutter'>
            <Col xs={12} md={3}>
              <ReportBreadCrumb />
            </Col>
          </Row>
          <Row className='button-gutter'>
            <Col xs={12}>
              <h1>End of Year Report</h1>
            </Col>
            <FormSubmissionMessage
              showSubmissionMessage={showSubmissionMessage}
              submissionMessage={notificationMessage}
              error={notificationError}
              hideSubmissionMessage={closeSubmissionMessage}
            />
          </Row>
        </Grid>
      </section>
      
      <section id='section--end-of-year-report' className='layout--section'>
        <Grid>
          <Row className='no-gutter top-gap'>
          <Col xs={12}>
            <Tabs 
              id='end-of-year-report-table-tabs' 
              activeKey={queryIndustryKey} 
              onSelect={(key) => setQueryIndustryKey(key)}
            >
              {industries.sort((industry1, industry2) => (industry1.id - industry2.id)).map((industry) => (
                <Tab key={industry.id} eventKey={industry.id} title={industry.name} />
              ))}
            </Tabs>
            <EndOfYearReportTable
              fetchQuery={fetchQuery}
              setFetchQuery={setFetchQuery}
              searchText={searchText}
              setSearchText={setSearchText}
              sortBy={sortBy}
              notificationButtonOnClick={notificationButtonOnClick}
              {...props}
            />
            </Col>
          </Row>
        </Grid>
      </section>
      <ConfirmationModal
        id="confirm-send-notification"
        show={showModal}
        actions={[{
          onConfirmAction: sendButtonOnClick,
          actionLabel: 'Send',
        }]}
        cancel={handleClose}
        message={<span>{modalBody}</span>}
        headingText={<span>Notification</span>}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  industries: state.industry.industries,
  isFetchingReport: state.admin.isFetchingReport,
  userCpdProgressReport: state.admin.report === undefined ? undefined : state.admin.report.userCpdProgressReport,
  isSendingNotification: state.admin.isSendingNotification,
  notificationError: state.admin.error,
});

const mapDispatchToProps = (dispatch) => ({
  getIndustries: bindActionCreators(industryActions.industries.get, dispatch),
  getUserCpdProgressReport: bindActionCreators(adminActions.admin.getUserCpdProgressReport, dispatch),
  sendUserCpdProgressNotification: bindActionCreators(adminActions.admin.sendUserCpdProgressNotification, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(EndOfYearReport);



// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/end-of-year-report.js